// Domain + form types for the Recommendations tab.

export type ItemType = "Task" | "Governance" | "Material";

export const ITEM_TYPES: { value: ItemType; label: string }[] = [
  { value: "Task", label: "Service" },
  { value: "Governance", label: "Governance" },
  { value: "Material", label: "Product" },
];

export const itemTypeLabel = (v: string): string =>
  ITEM_TYPES.find((o) => o.value === v)?.label ?? v;

export type ConditionType =
  | "always"
  | "greater_than"
  | "greater_than_or_equal_to"
  | "equal_to"
  | "less_than_or_equal_to"
  | "less_than";

// Comparators offered when "Only when" is chosen (excludes "always").
export const CONDITION_TYPES: {
  value: ConditionType;
  label: string;
  symbol: string;
}[] = [
  { value: "greater_than", label: "Greater than", symbol: ">" },
  {
    value: "greater_than_or_equal_to",
    label: "Greater than or equal to",
    symbol: ">=",
  },
  { value: "equal_to", label: "Equal to", symbol: "==" },
  {
    value: "less_than_or_equal_to",
    label: "Less than or equal to",
    symbol: "<=",
  },
  { value: "less_than", label: "Less than", symbol: "<" },
];

export type ComparisonValueType = "selected_value" | "number";

export interface Operand {
  slug: string;
  name: string;
}

export interface ItemOption {
  id: string;
  name: string; // bare name (list resolution)
  label: string; // dropdown label (services include category)
}

export interface Refinement {
  taskId: string;
  quantityValue: string; // operand slug
}

export interface Recommendation {
  id: string;
  quantityValue: string;
  itemId: string;
  itemType: ItemType;
  conditionType: ConditionType;
  conditionalValue: string;
  comparisonValue: string;
  comparisonValueType: ComparisonValueType;
  position: number;
  refinements: Refinement[];
}

export interface RecommendationFormValues {
  quantityValue: string;
  itemId: string;
  itemType: ItemType;
  conditionType: ConditionType;
  conditionalValue: string;
  comparisonValue: string;
  comparisonValueType: ComparisonValueType;
  // taskId -> quantity operand slug (only non-empty are persisted)
  refinements: Record<string, string>;
}

export const emptyRecommendationForm: RecommendationFormValues = {
  quantityValue: "",
  itemId: "",
  itemType: "Task",
  conditionType: "always",
  conditionalValue: "",
  comparisonValue: "",
  comparisonValueType: "selected_value",
  refinements: {},
};

export const conditionSummary = (r: {
  conditionType: string;
  conditionalValue: string;
  comparisonValue: string;
}): string => {
  if (r.conditionType === "always" || !r.conditionType) return "Always";
  const sym =
    CONDITION_TYPES.find((c) => c.value === r.conditionType)?.symbol ??
    r.conditionType;
  return `${r.conditionalValue} ${sym} ${r.comparisonValue}`.trim();
};
